<template>
  <section class="view-stack">
    <article class="dashboard-panel quota-records-panel">
      <div class="panel-heading quota-records-heading">
        <div>
          <h2>额度记录</h2>
        </div>
        <el-button
          icon="el-icon-refresh"
          :loading="loading"
          @click="loadRecords"
        >刷新</el-button>
      </div>

      <el-table
        v-loading="loading"
        :data="records"
        class="dashboard-table quota-records-table"
        empty-text="暂无额度记录"
      >
        <el-table-column label="类型" width="150">
          <template slot-scope="scope">
            <el-tag :type="recordTypeTag(scope.row.recordType)" size="small">
              {{ recordTypeLabel(scope.row.recordType) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="说明" min-width="240">
          <template slot-scope="scope">
            <span class="quota-record-description">{{ quotaRecordDescription(scope.row) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="业务日期" width="130">
          <template slot-scope="scope">
            {{ formatRecordDate(scope.row.recordDate, false) }}
          </template>
        </el-table-column>
        <el-table-column label="记录时间" width="180">
          <template slot-scope="scope">
            {{ formatRecordDate(scope.row.recordTime, true) }}
          </template>
        </el-table-column>
      </el-table>

      <div v-if="records.length" class="quota-records-mobile-list">
        <article v-for="record in records" :key="record.id || record.recordTime" class="quota-record-mobile-card">
          <div class="quota-record-mobile-card__header">
            <el-tag :type="recordTypeTag(record.recordType)" size="small">
              {{ recordTypeLabel(record.recordType) }}
            </el-tag>
            <strong>{{ formatRecordDate(record.recordDate, false) }}</strong>
          </div>
          <p>{{ quotaRecordDescription(record) }}</p>
          <div class="quota-record-mobile-card__time">记录时间：{{ formatRecordDate(record.recordTime, true) }}</div>
        </article>
      </div>
      <div v-else-if="!loading" class="quota-records-mobile-empty">暂无额度记录</div>

      <div v-if="pagination.total > 0" class="quota-records-pagination">
        <el-pagination
          background
          layout="total, prev, pager, next"
          :current-page="pagination.pageNo"
          :page-size="pagination.pageSize"
          :total="pagination.total"
          @current-change="handlePageChange"
        ></el-pagination>
      </div>
    </article>
  </section>
</template>

<script>
import { fetchQuotaRecords } from '../../api/quotaRecords';

const RECORD_TYPE_LABELS = {
  REGISTER: '注册赠送',
  CHECKIN: '每日赠送',
  PURCHASE: '套餐兑换',
  USAGE: '使用扣除',
  TIMEOUT_COMPENSATION: '超时赔付',
  MANUAL_GRANT: '额度增加'
};

export default {
  name: 'QuotaRecordsView',
  data() {
    return {
      loading: false,
      records: [],
      pagination: {
        pageNo: 1,
        pageSize: 20,
        total: 0
      }
    };
  },
  created() {
    this.loadRecords();
  },
  methods: {
    async loadRecords() {
      this.loading = true;
      try {
        const { data } = await fetchQuotaRecords({
          pageNo: this.pagination.pageNo,
          pageSize: this.pagination.pageSize
        });
        if (!data || data.code !== 0 || !data.data) {
          throw new Error((data && data.message) || '额度记录加载失败');
        }
        this.records = Array.isArray(data.data.records) ? data.data.records : [];
        this.pagination.total = Number(data.data.total || 0);
      } catch (error) {
        this.records = [];
        this.pagination.total = 0;
        this.$message.error(error.message || '额度记录加载失败');
      } finally {
        this.loading = false;
      }
    },
    handlePageChange(pageNo) {
      if (this.loading || pageNo === this.pagination.pageNo) return;
      this.pagination.pageNo = pageNo;
      this.loadRecords();
    },
    recordTypeLabel(recordType) {
      return RECORD_TYPE_LABELS[recordType] || recordType || '其他';
    },
    recordTypeTag(recordType) {
      if (recordType === 'USAGE') return 'info';
      if (recordType === 'TIMEOUT_COMPENSATION') return 'warning';
      return 'success';
    },
    quotaRecordDescription(row) {
      if (!row) return '-';
      const remark = String((row && row.remark) || '').trim();
      const rawAmount = Number(row.changeAmount);
      const amount = Number.isFinite(rawAmount) ? Math.abs(rawAmount).toFixed(2) : '-';

      if (row.recordType === 'MANUAL_GRANT') {
        return remark || `系统为您增加${amount}额度`;
      }
      if (row.recordType === 'TIMEOUT_COMPENSATION') {
        return remark || `超时赔付：原始费用 ${this.formatQuotaAmount(row.sourceAmount)}，返还 ${amount} 额度`;
      }
      if (row.recordType === 'REGISTER') {
        return `注册赠送额度 ${amount}`;
      }
      if (row.recordType === 'CHECKIN') {
        const giftRemark = remark.replace(/签到奖励/g, '每日赠送').replace(/签到/g, '领取');
        return row.consecutiveDays == null
          ? `${giftRemark || '每日赠送'}，赠送 ${amount} 额度`
          : `连续领取第 ${row.consecutiveDays} 天，赠送 ${amount} 额度`;
      }
      if (row.recordType === 'PURCHASE') {
        const packageLabel = remark ? `兑换「${remark}」` : '套餐兑换';
        if (row.groupType === 'MONTH_CARD' || row.quotaScope === 'CYCLE') {
          const details = [];
          if (row.dailyQuota != null) {
            details.push(`每日额度 ${this.formatQuotaAmount(row.dailyQuota)} 额度`);
          }
          if (row.periodStart) details.push(`生效时间 ${this.formatRecordDate(row.periodStart, true)}`);
          if (row.periodEnd) details.push(`到期时间 ${this.formatRecordDate(row.periodEnd, true)}`);
          details.push(`有效期总额度 ${amount} 额度`);
          return `${packageLabel}，${details.join('，')}`;
        }
        return `${packageLabel}，获得 ${amount} 额度`;
      }
      if (row.recordType === 'USAGE') {
        const groupLabel = row.groupType === 'MONTH_CARD'
          ? '日卡分组'
          : (row.groupType === 'BALANCE' ? '余额分组' : '额度分组');
        const usageLabel = row.requestCount == null
          ? (remark || '当日使用次数未知')
          : `当日累计使用 ${row.requestCount} 次`;
        return `${groupLabel}${usageLabel}，消耗额度 ${amount} 额度`;
      }
      return remark || '-';
    },
    formatQuotaAmount(value) {
      if (value === null || value === undefined || value === '') return '-';
      const amount = Number(value);
      return Number.isFinite(amount) ? amount.toFixed(2) : '-';
    },
    formatRecordDate(value, includeTime) {
      if (value === null || value === undefined || value === '') return '--';
      if (typeof value === 'string') {
        const match = value.trim().match(/^(\d{4}-\d{2}-\d{2})(?:[ T](\d{2}:\d{2}:\d{2}))?/);
        if (match && (!includeTime || match[2])) {
          return includeTime ? `${match[1]} ${match[2]}` : match[1];
        }
      }

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '--';
      const pad = part => String(part).padStart(2, '0');
      const datePart = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
      if (!includeTime) return datePart;
      return `${datePart} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    }
  }
};
</script>

<style scoped>
.quota-records-heading {
  align-items: center;
}

.quota-records-pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 18px;
}

.quota-record-description {
  white-space: normal;
  overflow-wrap: anywhere;
}

.quota-records-mobile-list,
.quota-records-mobile-empty {
  display: none;
}

@media (max-width: 640px) {
  .quota-records-heading {
    align-items: flex-start;
  }

  .quota-records-table {
    display: none;
  }

  .quota-records-mobile-list {
    display: grid;
    gap: 12px;
  }

  .quota-record-mobile-card {
    display: grid;
    gap: 9px;
    padding: 14px;
    border: 1px solid #deebe6;
    border-radius: 8px;
    background: #ffffff;
  }

  .quota-record-mobile-card__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }

  .quota-record-mobile-card__header strong {
    color: #526b78;
    font-size: 12px;
    font-weight: 600;
  }

  .quota-record-mobile-card p {
    margin: 0;
    color: #263f4a;
    line-height: 1.7;
    overflow-wrap: anywhere;
  }

  .quota-record-mobile-card__time {
    padding-top: 9px;
    color: #718590;
    border-top: 1px solid #e5efeb;
    font-size: 12px;
  }

  .quota-records-pagination {
    justify-content: center;
    width: 100%;
    overflow: hidden;
  }

  .quota-records-pagination .el-pagination {
    display: flex;
    width: 100%;
    justify-content: center;
    padding: 0;
    white-space: nowrap;
  }

  .quota-records-pagination .el-pagination__total {
    display: none;
  }

  .quota-records-mobile-empty {
    display: block;
    padding: 24px 12px;
    color: #748894;
    text-align: center;
  }
}
</style>
